import { useEffect, useRef, useState } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { PS, T } from '../lib/theme';

interface Props {
  open: boolean;
  title: string;
  message: string;
  /** Confirm button label. Defaults to "Confirm". */
  confirmLabel?: string;
  /** Cancel button label. Defaults to "Cancel". */
  cancelLabel?: string;
  /** Style the confirm action as destructive. */
  destructive?: boolean;
  /** Disable confirmation while a page-level action is pending. */
  confirmDisabled?: boolean;
  /** If true, show a reason textarea required for confirmation. */
  requireReason?: boolean;
  onConfirm: (reason: string) => Promise<void> | void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  confirmDisabled = false,
  requireReason = false,
  onConfirm,
  onCancel,
}: Props) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const reasonRef = useRef<HTMLTextAreaElement>(null);

  // Reset internal state when reopened.
  useEffect(() => {
    if (open) {
      setReason('');
      setBusy(false);
    }
  }, [open]);

  // Focus the reason field on open instead of using the autoFocus prop.
  useEffect(() => {
    if (open && requireReason) {
      reasonRef.current?.focus();
    }
  }, [open, requireReason]);

  const canConfirm = !busy && !confirmDisabled && (!requireReason || reason.trim().length > 0);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await onConfirm(reason.trim());
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onCancel} title={title}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <p style={{ ...T.ui, color: PS.nearBlack, margin: 0 }}>{message}</p>
        {requireReason && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={T.label}>Reason</span>
            <textarea
              ref={reasonRef}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              style={{
                padding: 8,
                border: `1px solid ${PS.border}`,
                borderRadius: 3,
                fontFamily: "'Barlow',sans-serif",
                fontSize: 14,
                resize: 'vertical',
              }}
            />
          </label>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            onClick={() => void handleConfirm()}
            disabled={!canConfirm}
          >
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
